import { useState } from 'react';
import { Link } from 'react-router';
import { getTaskOutbox } from '../../local';
import { writtenLabel, type OutboxItem } from '../../local/taskOutbox';
import { useTaskOutbox } from '../useTaskOutbox';
import { ActionButton } from '../ActionButton';

/**
 * This account's task comments/files waiting on this device (B4, owner
 * rulings 2026-10-25): "Waiting to send" while there's no signal; "Couldn't
 * send" with the reason when it can't be delivered — kept until discarded,
 * the comment text can be copied. One task's items on its page (taskId), or
 * every task's on the Tasks page (with links).
 */
export function OutboxList({ taskId }: { taskId?: number }) {
  const { items, sending } = useTaskOutbox();
  const shown = taskId === undefined ? items : items.filter((i) => i.taskId === taskId);
  if (shown.length === 0) return null;
  const failed = shown.filter((i) => i.state === 'failed').length;
  return (
    <section className="outbox" aria-label="Saved on this device">
      <h3>
        {taskId === undefined ? 'Saved on this device' : 'On this device'}
        <span className="muted">
          {' '}
          — {shown.length - failed > 0 && `${shown.length - failed} waiting to send${sending ? ' (sending…)' : ''}`}
          {shown.length - failed > 0 && failed > 0 && ' · '}
          {failed > 0 && `${failed} couldn’t be sent`}
        </span>
      </h3>
      <ul>
        {shown.map((i) => (
          <OutboxRow key={i.id} item={i} withTask={taskId === undefined} sending={sending} />
        ))}
      </ul>
    </section>
  );
}

function OutboxRow({ item, withTask, sending }: { item: OutboxItem; withTask: boolean; sending: boolean }) {
  const [copied, setCopied] = useState(false);
  const what =
    item.kind === 'comment'
      ? `💬 “${preview(item.body ?? '')}”`
      : `📎 ${item.fileTitle || item.fileName || (item.fileKind === 'pdf' ? 'PDF' : 'Photo')}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.body ?? '');
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const discard = async () => {
    const message =
      item.state === 'failed'
        ? 'Remove this from the device? It was not sent.'
        : 'Remove this from the device? It hasn’t been sent yet and never will be.';
    if (window.confirm(message)) await getTaskOutbox().discard(item.id);
  };
  return (
    <li className={item.state === 'failed' ? 'outbox-failed' : undefined}>
      <div>
        {withTask && (
          <>
            <Link to={`/tasks/${item.taskId}`}>{item.taskTitle || `Task ${item.taskId}`}</Link>:{' '}
          </>
        )}
        {what}
      </div>
      <div className="muted outbox-meta">
        {item.state === 'failed' ? (
          <span className="error">Couldn’t send: {item.error || 'refused.'}</span>
        ) : item.uploaded ? (
          'Uploaded — finishing…'
        ) : (
          'Waiting to send'
        )}{' '}
        · written {writtenLabel(item.writtenAt)}
        {item.kind === 'comment' && (
          <button type="button" className="btn btn-small" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy text'}
          </button>
        )}
        {/* Not while it may be going out right now (it could still arrive). */}
        <ActionButton
          className="btn btn-small"
          busyLabel="Removing…"
          disabled={sending && item.state === 'waiting'}
          onAction={discard}
        >
          Discard
        </ActionButton>
      </div>
    </li>
  );
}

function preview(text: string): string {
  const one = text.replace(/\s+/g, ' ').trim();
  return one.length > 140 ? `${one.slice(0, 139)}…` : one;
}
